/**
 * One receipt, in whatever state it is in.
 *
 * While extraction runs this page polls the receipt itself and shows progress. Once read -- or
 * once reading failed, so it can be typed in beside its photo -- it is the review screen. Once
 * confirmed it is read-only except for the orders its lines were bought for, with a way to
 * reopen it.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronLeft, LoaderCircle, PenLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Notice } from "@/components/ui/Field";
import { businessesQuery } from "@/features/settings/queries";
import { Amount, PurchaseDate, StatusBadge, StoreName } from "./ReceiptBits";
import { ReceiptImage } from "./ReceiptImage";
import { canAttach } from "./allocation";
import { LineOrders } from "./LineOrders";
import { receiptAllocationsQuery, receiptQuery, useReopenReceipt, type Receipt } from "./queries";
import { ReviewScreen } from "./ReviewScreen";
import { isWorking, SLOW_AFTER_MS } from "./status";

export function ReceiptPage() {
  const { t } = useTranslation();
  const { receiptId } = useParams({ from: "/app/receipts/$receiptId" });
  const receipt = useQuery(receiptQuery(receiptId));

  return (
    <section className="space-y-5">
      <Link
        to="/receipts"
        className="-ml-1 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        {t("receipts.title")}
      </Link>

      {receipt.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {receipt.isError && <Notice tone="error">{t("receipts.errors.loadFailed")}</Notice>}
      {receipt.data && <ReceiptBody receipt={receipt.data} />}
    </section>
  );
}

function ReceiptBody({ receipt }: { receipt: Receipt }) {
  const { t } = useTranslation();

  return (
    <>
      <header className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-semibold text-stone-900">
            <StoreName name={receipt.store_name} />
          </h1>
          <StatusBadge status={receipt.status} />
        </div>
      </header>

      {isWorking(receipt.status) && <Extracting />}

      {(receipt.status === "needs_review" || receipt.status === "extraction_failed") && (
        <>
          <ReviewScreen receipt={receipt} />
          {receipt.status === "extraction_failed" && (
            <Link
              to="/receipts/manual"
              className="text-brand-700 flex items-center justify-center gap-2 text-sm underline"
            >
              <PenLine className="h-4 w-4" aria-hidden />
              {t("receipts.review.startOver")}
            </Link>
          )}
        </>
      )}

      {receipt.status === "confirmed" && <Confirmed receipt={receipt} />}
    </>
  );
}

/** A confirmed receipt as the ledger holds it, and the way back to review for a mistake. */
function Confirmed({ receipt }: { receipt: Receipt }) {
  const { t } = useTranslation();
  const businesses = useQuery(businessesQuery);
  const reopen = useReopenReceipt(receipt.id);
  const allocations = useQuery(receiptAllocationsQuery(receipt.id));
  const [error, setError] = useState<string | undefined>();
  const businessName = (id: string | null) =>
    businesses.data?.find((business) => business.id === id)?.name ?? "";

  async function goBack() {
    setError(undefined);
    try {
      await reopen.mutateAsync();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("receipts.errors.saveFailed"));
    }
  }

  return (
    <>
      <p className="text-sm text-stone-600">
        <PurchaseDate date={receipt.purchase_date} />
        {receipt.store_address && ` · ${receipt.store_address}`}
      </p>

      <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
        {receipt.lines.map((line) => (
          <li key={line.id} className="space-y-2 px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-stone-800">{line.description || line.raw_text}</p>
                <p className="text-xs text-stone-500">
                  {line.bucket_type === "household"
                    ? t("settings.categories.household")
                    : businessName(line.business_id)}
                </p>
              </div>
              <Amount money={line.line_total} className="text-stone-800" />
            </div>
            {/* Which order a purchase was for is often known only later, so it stays open
                after confirming, without reopening the receipt. */}
            {canAttach(line) && (
              <LineOrders
                receiptId={receipt.id}
                line={line}
                allocations={(allocations.data ?? []).filter(
                  (allocation) => allocation.receipt_line_id === line.id,
                )}
                disabled={false}
              />
            )}
          </li>
        ))}
      </ul>

      <dl className="space-y-1.5 rounded-lg border border-stone-200 bg-white px-4 py-3">
        <Total label={t("receipts.detail.subtotal")} money={receipt.subtotal} />
        {receipt.tax_lines.map((tax) => (
          <Total key={tax.id} label={tax.label} money={tax.amount} />
        ))}
        <Total label={t("receipts.detail.total")} money={receipt.total} strong />
      </dl>

      {receipt.notes && (
        <p className="text-sm whitespace-pre-line text-stone-600">{receipt.notes}</p>
      )}

      {receipt.has_image ? (
        <ReceiptImage receiptId={receipt.id} />
      ) : (
        receipt.source !== "manual" && (
          <p className="text-sm text-stone-500">{t("receipts.review.imageDeleted")}</p>
        )
      )}

      {error && <Notice tone="error">{error}</Notice>}
      <Button
        type="button"
        variant="secondary"
        disabled={reopen.isPending}
        onClick={() => void goBack()}
      >
        {t("receipts.review.reopen")}
      </Button>
    </>
  );
}

function Extracting() {
  const { t } = useTranslation();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="space-y-3">
      <p role="status" className="flex items-center gap-2 text-stone-700">
        <LoaderCircle className="text-brand-700 h-5 w-5 animate-spin" aria-hidden />
        {t("receipts.detail.extracting")}
      </p>
      {slow && <Notice tone="info">{t("receipts.detail.slow")}</Notice>}
    </div>
  );
}

function Total({
  label,
  money,
  strong = false,
}: {
  label: string;
  money: Receipt["total"];
  strong?: boolean;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className={strong ? "font-semibold text-stone-900" : "text-stone-600"}>{label}</dt>
      <dd>
        <Amount money={money} className={strong ? "font-semibold text-stone-900" : ""} />
      </dd>
    </div>
  );
}
